import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { C } from '../../albumColors';
import { albumService } from '../../../services/api';
import { userColor, userInitial } from '../../albumData';

const PREVIEW_COUNT = 3;

/**
 * RightPanel preview: hasta {PREVIEW_COUNT} ofertas que coinciden con el usuario.
 * Click en una → /album?offerId={id} (banner del álbum).
 * Click en "Ver todas" → /album/dashboard.
 */
function MatchingOffersCard({ allUsers, theme }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [offers, setOffers] = useState([]);

  const headingText = theme?.cardHeadingText ?? C.onSurfaceVariant;
  const bodyText    = theme?.cardBodyText    ?? C.onSurface;
  const subtleText  = theme?.cardSubtleText  ?? C.outline;
  const rowBorder   = theme?.cardRowBorder   ?? `${C.outlineVariant}22`;
  const hoverBg     = theme?.cardRowHoverBg  ?? C.surfaceContainerHigh;
  const accent      = theme?.primaryAccent   ?? C.primary;
  const accentDim   = theme?.primaryAccentDim ?? `${C.primary}22`;

  useEffect(() => {
    let cancelled = false;
    albumService.listOffers('matching')
      .then(res => { if (!cancelled) setOffers(Array.isArray(res.data) ? res.data : []); })
      .catch(() => { /* silent */ });
    return () => { cancelled = true; };
  }, []);

  const namesById = React.useMemo(() => {
    const m = new Map();
    for (const u of allUsers || []) m.set(u.id, u.name);
    return m;
  }, [allUsers]);

  // Si el sidebar tiene un filtro de comunidad activo, allUsers ya viene
  // recortado a esa comunidad. Restringimos las ofertas mostradas a las que
  // publica alguien dentro de ese set; si no hay filtro, allUsers contiene a
  // todos los usuarios alcanzables y la lista queda igual.
  const visibleOffers = React.useMemo(() => {
    return offers.filter(o => namesById.has(o.userId));
  }, [offers, namesById]);

  return (
    <section className="mb-8">
      <div className="flex items-baseline justify-between mb-4">
        <h4 className="text-xs font-bold uppercase tracking-widest" style={{ color: headingText }}>
          {t('Ofertas para vos')}
          {visibleOffers.length > 0 && (
            <span className="ml-2 px-1.5 py-0.5 rounded-full text-[9px]"
                  style={{ backgroundColor: accentDim, color: accent }}>
              {visibleOffers.length}
            </span>
          )}
        </h4>
        <button
          onClick={() => navigate('/album/dashboard')}
          className="text-[10px] font-bold uppercase tracking-wide hover:opacity-70 transition-opacity"
          style={{ color: accent }}
        >
          {t('Ver todas')}
        </button>
      </div>

      {visibleOffers.length === 0 ? (
        <p className="text-xs" style={{ color: subtleText }}>
          {t('Sin ofertas que coincidan')}
        </p>
      ) : (
        <div className="space-y-2">
          {visibleOffers.slice(0, PREVIEW_COUNT).map(o => {
            const name = namesById.get(o.userId) || `User ${o.userId}`;
            return (
              <button
                key={o.id}
                onClick={() => navigate(`/album?offerId=${o.id}`)}
                className="text-left w-full flex items-center gap-2 p-2 rounded-lg transition-all"
                style={{ border: `1px solid ${rowBorder}` }}
                onMouseEnter={e => e.currentTarget.style.backgroundColor = hoverBg}
                onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                <div
                  className="w-6 h-6 rounded-full flex items-center justify-center text-white text-[9px] font-bold shrink-0"
                  style={{ backgroundColor: userColor(o.userId) }}
                >
                  {userInitial(name)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[11px] font-bold truncate" style={{ color: bodyText }}>{name}</p>
                  <p className="text-[9px]" style={{ color: subtleText }}>
                    {t('da {{n}}', { n: o.offered.length })} · {t('pide {{n}}', { n: o.requested.length })}
                  </p>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </section>
  );
}

export default MatchingOffersCard;
